import { Checkbox } from 'cpool-components/input/Checkbox';
import { EUROPACE_INSURED_RISKS } from 'cpool-constants';
import { objectKeys } from 'cpool-schemas';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { BankLogo } from '../leadDetails/components/Overview/OfferManagement/Table/Cells/BankLogo';
import { OfferManagementTableData } from '../leadDetails/components/Overview/OfferManagement/Table/Cells/OfferManagementTableData';
import { currencyFormat, dateTimeShortFormat, numberFormat } from '~/lib/i18n';

type OfferRowProps = {
    payload: OfferWithOrder;
    offer: OfferWithOrder;
    hasSecondApplicant?: boolean;
    index: number;
    isSelected: boolean;
    onCheckboxChange: (offerId: string, checked: boolean) => void;
    onDragStart: (index: number) => void;
    onDragOver: (e: React.DragEvent<HTMLTableRowElement>) => void;
    onDrop: (e: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => void;
};

type OfferWithOrder = SchemaLoanRequestResolver_GetAcceptedOffers[number] & { order: number };

type ResidualDebtLightsProps = {
    insuredRisks: OfferWithOrder['applicant1ResidualDebtInsurance'];
    testIdPrefix: string;
};

const ResidualDebtLights: React.FC<ResidualDebtLightsProps> = ({ insuredRisks, testIdPrefix }) => (
    <div className="flex items-center gap-0.5">
        {objectKeys(EUROPACE_INSURED_RISKS).map((risk) => (
            <span
                key={risk}
                data-testid={`${testIdPrefix}-${risk}`}
                className={`size-2.5 rounded-full ${
                    insuredRisks?.includes(risk) ? 'bg-green' : 'bg-grey-outline-light'
                }`}
            />
        ))}
    </div>
);

export const OfferRow: React.FC<OfferRowProps> = ({
    hasSecondApplicant = false,
    index,
    isSelected,
    offer,
    onCheckboxChange,
    onDragOver,
    onDragStart,
    onDrop,
}) => {
    const { t } = useTranslation();

    const [date] = dateTimeShortFormat(new Date(offer.acceptedAt ?? 0)).split(',');

    return (
        <tr
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, index)}
            className="relative h-20 cursor-move whitespace-nowrap border-b border-grey-outline-light bg-white text-sm text-midnight transition-colors hover:bg-surface-light [&>td]:overflow-hidden [&>td]:px-4 [&>td]:py-4 [&>td]:text-left [&>td]:text-sm [&>td]:text-ellipsis [&>td]:text-midnight [&>td:nth-child(2)]:text-center [&>td:nth-child(2)]:[&_div]:text-center"
        >
            <OfferManagementTableData>
                <div className="flex justify-center">
                    <Checkbox
                        id={`email-offer-${offer.id}`}
                        aria-label={t('MESSAGE.EMAIL.SELECT_OFFER')}
                        checked={isSelected}
                        onChange={(e) => onCheckboxChange(offer.id, e.target.checked)}
                    />
                </div>
            </OfferManagementTableData>
            <BankLogo
                bankLogo={'bankLogo' in offer ? offer.bankLogo : ''}
                bankName={'bank' in offer ? offer.bank : ''}
                description={'productDescription' in offer ? offer.productDescription : null}
            />
            <OfferManagementTableData>{date}</OfferManagementTableData>
            <OfferManagementTableData className="max-w-[100px] overflow-hidden whitespace-nowrap text-ellipsis">
                {'providerApplicationNumber' in offer ? offer.providerApplicationNumber : null}
            </OfferManagementTableData>
            <OfferManagementTableData>{currencyFormat(offer.creditAmount ?? 0)}</OfferManagementTableData>
            <OfferManagementTableData>
                {t('OFFER_MANAGEMENT.RUNTIME', { count: offer.runtime ?? 0 })}
            </OfferManagementTableData>
            <OfferManagementTableData>{currencyFormat(offer.rate ?? 0)}</OfferManagementTableData>
            <OfferManagementTableData>
                {t('OFFER_MANAGEMENT.INTEREST', { interest: numberFormat(offer.interest ?? 0) })}
            </OfferManagementTableData>
            <OfferManagementTableData>
                <div className="flex flex-col items-center gap-0.5">
                    <ResidualDebtLights
                        insuredRisks={offer.applicant1ResidualDebtInsurance}
                        testIdPrefix="applicant-traffic-light"
                    />
                    {hasSecondApplicant && (
                        <ResidualDebtLights
                            insuredRisks={offer.applicant2ResidualDebtInsurance}
                            testIdPrefix="applicant-2-traffic-light"
                        />
                    )}
                </div>
            </OfferManagementTableData>
        </tr>
    );
};
